import { addDoc, collection, getFirestore } from 'firebase/firestore';
import { getApp } from 'firebase/app';
import {
  formatMarketingFromHeader,
  MARKETING_CAMPAIGN_FROM_EMAIL,
  MARKETING_CAMPAIGN_FROM_NAME,
} from '@otw/models/leads/marketingCampaign';
import { fileToBase64 } from '@/core/utils/fileUtils';
import { buildTempTagMailFields } from '../tempTagRecipients';
import { tempTagRequestEmail } from './tempTagRequestEmail';

export interface TempTagPortalAttachment {
  url: string;
  fileName: string;
}

export interface SendTempTagRequestEmailInput {
  to: string;
  cc: string;
  subject: string;
  body: string;
  vins: string[];
  senderName: string;
  portalAttachments: TempTagPortalAttachment[];
  additionalFile?: File | null;
}

interface MailAttachment {
  filename: string;
  content: string;
  encoding: 'base64';
}

const urlToBase64 = async (url: string): Promise<string> => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch file: ${response.statusText}`);
  }

  const blob = await response.blob();

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result as string;
      resolve(base64String.split(',')[1] || base64String);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

export const sendTempTagRequestEmail = async (
  input: SendTempTagRequestEmailInput,
): Promise<boolean> => {
  try {
    const attachments: MailAttachment[] = [];

    for (const attachment of input.portalAttachments) {
      const content = await urlToBase64(attachment.url);
      attachments.push({
        filename: attachment.fileName,
        content,
        encoding: 'base64',
      });
    }

    if (input.additionalFile) {
      const content = await fileToBase64(input.additionalFile);
      attachments.push({
        filename: input.additionalFile.name,
        content,
        encoding: 'base64',
      });
    }

    const recipients = buildTempTagMailFields(input.to, input.cc);
    if (recipients.to.length === 0) {
      console.error('Temp tag request email was not queued: no recipients');
      return false;
    }

    const { html } = await tempTagRequestEmail({
      vins: input.vins,
      senderName: input.senderName,
    });

    const db = getFirestore(getApp());
    const from = formatMarketingFromHeader(
      MARKETING_CAMPAIGN_FROM_NAME,
      MARKETING_CAMPAIGN_FROM_EMAIL,
    );

    // One mail document with to as a string array — same pattern as business
    // inquiry. A comma-joined string is treated as a single address.
    const docRef = await addDoc(collection(db, 'mail'), {
      to: recipients.to,
      ...(recipients.cc ? { cc: recipients.cc } : {}),
      ...(recipients.replyTo ? { replyTo: recipients.replyTo } : {}),
      message: {
        ...(from ? { from } : {}),
        subject: input.subject,
        html,
        text: input.body,
        ...(attachments.length > 0 ? { attachments } : {}),
      },
    });

    console.info('Queued temp tag request email', {
      mailId: docRef.id,
      recipientCount: recipients.to.length,
      senderCopy: Boolean(recipients.cc),
    });
    return true;
  } catch (error) {
    console.error('Error sending temp tag request email:', error);
    return false;
  }
};
